import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { DirectCell, CellFunction } from '../../cells';
import Tooltip from '../../../tooltip';
import { IEpxPPDepress } from '../../../../../libs/apps/epxppdepress';
import moment from 'moment-timezone';
import { GetCurrentTZ } from '../../../../../libs/time';
import Icon from '../../../../../components/atoms/icon';
import { faTimes, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useTerms } from '../../../../../contexts/terms';

const CURRENT_TZ = GetCurrentTZ();

const AttemptTooltips = {
  0: 'We have reached out to the mother once, we will try 2 more times if the mother does not respond.',
  1: 'We have reached out to the mother 2 times, we will try 1 more time if the mother does not respond.',
  2: 'We have reached out to the mother 3 times, an alert will be triggered if no response by then.'
};

const AttemptsCell: CellFunction<IEpxPPDepress> = ({
  row: { original: session }
}) => {
  if (!session.retryCount) {
    return <span>1</span>;
  }
  let tooltip = 'We have reached out to the mother multiple times.';
  if (AttemptTooltips[session.retryCount]) {
    tooltip = AttemptTooltips[session.retryCount];
  }
  return (
    <Tooltip content={<span>{tooltip}</span>}>
      <span>{session.retryCount + 1}/3</span>
    </Tooltip>
  );
};

const DaysUntilNextMessageCell: CellFunction<IEpxPPDepress> = ({
  row: { original: session }
}) => {
  const { terms } = useTerms();
  if (session.readyToAnswer === null || session.readyToAnswer === false) {
    const tz = session?.schedule?.tz || CURRENT_TZ;
    const daysUntilNextMessage = moment
      .tz(session.time as Date, tz)
      .diff(moment.tz(tz), 'days');
    if (daysUntilNextMessage <= 0 || daysUntilNextMessage === null) {
      return (
        <Tooltip
          content={`This ${terms.patient.default} has not responded to the survey`}
        >
          <span>
            <Icon icon={faTimes} />
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip
        content={`This ${terms.patient.default} has delayed taking the survey. We will reach out again to take this survey.`}
      >
        <span>{daysUntilNextMessage}</span>
      </Tooltip>
    );
  } else if (session.readyToAnswer === true) {
    return (
      <Tooltip
        content={`This ${terms.patient.default} has responded to the survey`}
      >
        <span>
          <Icon icon={faCheck} />
        </span>
      </Tooltip>
    );
  }
  return null;
};

const EpxPPDepressTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxPPDepress>[] = [
    {
      Header: 'Attempts',
      id: 'retryCount',
      Cell: AttemptsCell
    },
    {
      Header: 'Q1',
      id: 'q1',
      Cell: ResponseGuard(DirectCell('q1') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q2',
      id: 'q2',
      Cell: ResponseGuard(DirectCell('q2') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q3',
      id: 'q3',
      Cell: ResponseGuard(DirectCell('q3') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q4',
      id: 'q4',
      Cell: ResponseGuard(DirectCell('q4') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q5',
      id: 'q5',
      Cell: ResponseGuard(DirectCell('q5') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q6',
      id: 'q6',
      Cell: ResponseGuard(DirectCell('q6') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q7',
      id: 'q7',
      Cell: ResponseGuard(DirectCell('q7') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q8',
      id: 'q8',
      Cell: ResponseGuard(DirectCell('q8') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q9',
      id: 'q9',
      Cell: ResponseGuard(DirectCell('q9') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Q10',
      id: 'q10',
      Cell: ResponseGuard(DirectCell('q10') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Total',
      id: 'total',
      Cell: ResponseGuard(DirectCell('total') as CellFunction<IEpxPPDepress>)
    },
    {
      Header: 'Days Until Next Message',
      id: 'daysUntilNextMessage',
      Cell: DaysUntilNextMessageCell
    }
  ];
  return (
    <SessionTable<IEpxPPDepress>
      url="/epxppdepress/data/"
      columns={columns}
      disableFuture={true}
      {...props}
    />
  );
};

export default EpxPPDepressTable;
